import type { StudioAdminPricingDetail } from '../lib/api';

type StudioPricingTabsProps = {
  pricing: StudioAdminPricingDetail | undefined;
  isLoading?: boolean;
  canSync?: boolean;
  isSyncing?: boolean;
  onSync?: () => void;
  canEditSell?: boolean;
  isCreatingDraft?: boolean;
  isPublishing?: boolean;
  isRollingBack?: boolean;
  onCreateDraft?: () => void;
  onPublishDraft?: () => void;
  onRollback?: (version: number) => void;
};

function formatUsd(value: number | null | undefined): string {
  if (value == null) return '—';
  return `$${value.toFixed(4)}`;
}

export function StudioPricingTabs({
  pricing,
  isLoading,
  canSync,
  isSyncing,
  onSync,
  canEditSell,
  isCreatingDraft,
  isPublishing,
  isRollingBack,
  onCreateDraft,
  onPublishDraft,
  onRollback,
}: StudioPricingTabsProps) {
  if (isLoading) {
    return <p className="text-sm text-ops-muted">Загрузка pricing…</p>;
  }
  if (!pricing) {
    return <p className="text-sm text-ops-muted">Pricing недоступен для этой модели</p>;
  }

  const policy = pricing.baseline_policy;

  return (
    <div className="space-y-4">
      <section className="ops-card space-y-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold">Provider price (COGS)</h2>
          <div className="flex items-center gap-2">
            {canSync ? (
              <button
                type="button"
                className="rounded-lg border border-ops-border px-2 py-0.5 text-xs"
                disabled={isSyncing}
                onClick={onSync}
              >
                {isSyncing ? 'Syncing…' : 'Sync provider price'}
              </button>
            ) : null}
            <span className="rounded-full bg-ops-surface px-2 py-0.5 text-xs text-ops-muted">
              {pricing.provider.source}
            </span>
          </div>
        </div>
        <dl className="grid gap-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-ops-muted">Status</dt>
            <dd>{pricing.provider.status ?? '—'}</dd>
          </div>
          {pricing.provider.source_type ? (
            <div className="flex justify-between gap-4">
              <dt className="text-ops-muted">Source type</dt>
              <dd>{pricing.provider.source_type}</dd>
            </div>
          ) : null}
          <div className="flex justify-between gap-4">
            <dt className="text-ops-muted">Currency</dt>
            <dd>{pricing.provider.currency ?? '—'}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-ops-muted">Provider model</dt>
            <dd className="truncate">{pricing.provider.provider_model_id ?? '—'}</dd>
          </div>
          {pricing.provider.source_url ? (
            <div className="flex justify-between gap-4">
              <dt className="text-ops-muted">Source</dt>
              <dd>
                <a
                  href={pricing.provider.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-ops-accent hover:underline"
                >
                  Open
                </a>
              </dd>
            </div>
          ) : null}
          <div className="flex justify-between gap-4">
            <dt className="text-ops-muted">Fetched</dt>
            <dd>{pricing.provider.fetched_at ?? '—'}</dd>
          </div>
          {(pricing.provider.sku_rules?.length ?? 0) > 0 ? (
            <div className="space-y-1 border-t border-ops-border pt-2">
              <p className="text-xs font-medium text-ops-muted">Provider SKU rules</p>
              <ul className="space-y-1 text-xs">
                {pricing.provider.sku_rules?.map((rule) => (
                  <li key={rule.id} className="rounded bg-ops-surface px-2 py-1">
                    <span className="font-medium">{rule.id}</span>
                    {rule.when ? (
                      <span className="text-ops-muted"> · {JSON.stringify(rule.when)}</span>
                    ) : null}
                    <span className="text-ops-muted">
                      {' '}
                      · {rule.billables.join(', ')} @ {rule.unit_prices.join(', ')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </dl>
        {pricing.provider.normalized_pricing ? (
          <pre className="max-h-48 overflow-auto rounded-lg bg-ops-surface p-3 text-xs text-ops-muted">
            {JSON.stringify(pricing.provider.normalized_pricing, null, 2)}
          </pre>
        ) : null}
      </section>

      <section className="ops-card space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold">Sell price (credits)</h2>
          {canEditSell ? (
            <div className="flex flex-wrap gap-2">
              {!pricing.sell.draft_version ? (
                <button
                  type="button"
                  className="rounded-lg border border-ops-border px-2 py-0.5 text-xs"
                  disabled={isCreatingDraft}
                  onClick={onCreateDraft}
                >
                  {isCreatingDraft ? 'Creating…' : 'Create draft'}
                </button>
              ) : (
                <button
                  type="button"
                  className="rounded-lg bg-ops-accent px-2 py-0.5 text-xs text-white disabled:opacity-50"
                  disabled={isPublishing || pricing.validation.cogs_errors.length > 0}
                  onClick={onPublishDraft}
                >
                  {isPublishing ? 'Publishing…' : `Publish v${pricing.sell.draft_version}`}
                </button>
              )}
            </div>
          ) : null}
        </div>
        <dl className="grid gap-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-ops-muted">Published version</dt>
            <dd>{pricing.sell.published_version ?? (pricing.sell.version || '—')}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-ops-muted">Draft version</dt>
            <dd>{pricing.sell.draft_version ?? '—'}</dd>
          </div>
          {(pricing.sell.draft_diff?.length ?? 0) > 0 ? (
            <div className="space-y-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
              <p className="font-medium text-amber-700">
                Draft diff (published v{pricing.sell.published_version ?? '—'} → draft v
                {pricing.sell.draft_version})
              </p>
              <table className="w-full text-left">
                <thead className="text-ops-muted">
                  <tr>
                    <th className="py-1 pr-2">Sample</th>
                    <th className="py-1 pr-2">Published</th>
                    <th className="py-1 pr-2">Draft</th>
                    <th className="py-1">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {pricing.sell.draft_diff?.map((row) => (
                    <tr key={row.label} className="border-t border-ops-border">
                      <td className="py-1 pr-2">{row.label}</td>
                      <td className="py-1 pr-2">{row.published_unit_credits}</td>
                      <td className="py-1 pr-2">{row.draft_unit_credits}</td>
                      <td className="py-1">
                        {row.delta_credits > 0 ? '+' : ''}
                        {row.delta_credits}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <div className="flex justify-between gap-4">
            <dt className="text-ops-muted">Pricing mode</dt>
            <dd>{pricing.sell.pricing_mode}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-ops-muted">FX USD/RUB</dt>
            <dd>{policy.fxUsdRub}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-ops-muted">Target COGS ratio</dt>
            <dd>{policy.targetCogsRatio}</dd>
          </div>
        </dl>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-ops-muted">
              <tr>
                <th className="py-1 pr-2">Sample</th>
                <th className="py-1 pr-2">COGS USD</th>
                <th className="py-1 pr-2">Unit cr</th>
                <th className="py-1">Total cr</th>
              </tr>
            </thead>
            <tbody>
              {pricing.sell.quote_samples.map((sample) => (
                <tr key={sample.label} className="border-t border-ops-border">
                  <td className="py-1.5 pr-2">{sample.label}</td>
                  <td className="py-1.5 pr-2">{formatUsd(sample.provider_cost_usd)}</td>
                  <td className="py-1.5 pr-2">{sample.unit_credits}</td>
                  <td className="py-1.5">{sample.total_credits}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pricing.validation.cogs_errors.length > 0 ? (
          <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 text-xs text-red-600">
            <p className="font-medium">COGS validation</p>
            <ul className="mt-1 list-disc pl-4">
              {pricing.validation.cogs_errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-xs text-green-600">COGS validation: OK</p>
        )}

        {(pricing.matrix?.length ?? 0) > 0 ? (
          <div className="space-y-2 border-t border-ops-border pt-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium">Pricing matrix (shadow)</p>
              <div className="flex items-center gap-2 text-xs">
                <span className="rounded-full bg-ops-surface px-2 py-0.5 text-ops-muted">
                  Read-only validation
                </span>
                <span
                  className={
                    pricing.validation.matrix_valid
                      ? 'text-green-600'
                      : 'text-amber-600'
                  }
                >
                  {pricing.validation.matrix_valid ? 'Matrix OK' : 'Matrix issues'}
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-ops-muted">
                  <tr>
                    <th className="py-1 pr-2">Sample</th>
                    <th className="py-1 pr-2">COGS USD</th>
                    <th className="py-1 pr-2">Legacy</th>
                    <th className="py-1 pr-2">Unified</th>
                    <th className="py-1 pr-2">Production</th>
                    <th className="py-1">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {pricing.matrix?.map((row) => (
                    <tr key={row.label} className="border-t border-ops-border">
                      <td className="py-1.5 pr-2">{row.label}</td>
                      <td className="py-1.5 pr-2">{formatUsd(row.provider_cost_usd)}</td>
                      <td className="py-1.5 pr-2">{row.legacy_unit_credits}</td>
                      <td className="py-1.5 pr-2">{row.unified_unit_credits ?? '—'}</td>
                      <td className="py-1.5 pr-2">{row.production_unit_credits}</td>
                      <td className="py-1.5">
                        {row.gross_margin_pct != null ? `${row.gross_margin_pct.toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(pricing.validation.matrix_errors?.length ?? 0) > 0 ? (
              <ul className="list-disc pl-4 text-xs text-amber-600">
                {pricing.validation.matrix_errors?.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {(pricing.sell.archived_versions?.length ?? 0) > 0 && canEditSell ? (
          <div className="space-y-2 border-t border-ops-border pt-3">
            <p className="text-xs font-medium text-ops-muted">Rollback</p>
            <div className="flex flex-wrap gap-2">
              {pricing.sell.archived_versions?.map((version) => (
                <button
                  key={version}
                  type="button"
                  className="rounded-lg border border-ops-border px-2 py-0.5 text-xs"
                  disabled={isRollingBack}
                  onClick={() => onRollback?.(version)}
                >
                  v{version}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
